import classNames from 'classnames'

import Button from '@/components/base/Button'
import Container from '@/components/base/Container'
import Grid from '@/components/base/Grid'
import Image from '@/components/base/Image'
import ImageParallax from '@/components/base/ImageParallax'
import Motion from '@/components/base/Motion'
import Typography, { TypographyProps } from '@/components/base/Typography'

import { pushParagraphRevealAnimation } from './animations'
import { Section, WPImage, WPLink } from '@/types'

type PushParagraphProps = Section &
  Partial<{
    image: WPImage
    alignment: 'left' | 'right'
    image_shape: 'square'
    title_variant: TypographyProps['variant']
    title_component: TypographyProps['component']
    title: string
    paragraph: string
    button: WPLink
  }>

const PushParagraph = ({
  image,
  title,
  title_variant,
  title_component,
  paragraph,
  alignment,
  image_shape,
  button,
  ...props
}: PushParagraphProps) => {
  return (
    <Motion animation={pushParagraphRevealAnimation}>
      {(motionRef) => (
        <section ref={motionRef} {...props}>
          <Container>
            <Grid className="items-center gap-y-sm">
              <div
                className={classNames(
                  'col-span-2 md:col-span-3 lg:col-span-4',
                  {
                    'md:order-1': alignment === 'left',
                    'md:order-2 lg:col-start-9': alignment === 'right'
                  }
                )}
              >
                {image?.url ? (
                  <ImageParallax
                    className={classNames({
                      'rounded-xxs': image_shape === 'square'
                    })}
                    data-motion-parallax
                  >
                    {(ref) => (
                      <Image
                        ref={ref}
                        ratio="1:1"
                        size="full"
                        loading="lazy"
                        source={image}
                        alt={image?.alt}
                        className={classNames({
                          'rounded-xxs': image_shape === 'square'
                        })}
                      />
                    )}
                  </ImageParallax>
                ) : null}
              </div>
              <div
                className={classNames('col-span-2', {
                  'md:order-2 md:col-span-3 lg:col-span-7 lg:col-start-6':
                    alignment === 'left',
                  'md:order-1 md:col-span-3 lg:col-span-7':
                    alignment === 'right' && image?.url,
                  'md:col-span-6 lg:col-span-12':
                    alignment === 'right' && !image?.url
                })}
              >
                <div className="grid gap-md">
                  {title ? (
                    <Typography
                      variant={title_variant}
                      component={title_component}
                      className="uppercase text-white [&_em]:text-blue-soft"
                      data-motion-title
                    >
                      {title}
                    </Typography>
                  ) : null}

                  {paragraph ? (
                    <Typography
                      variant="body"
                      className="block text-neutral-80 [&_ul>br]:hidden"
                      data-motion-paragraph
                    >
                      {paragraph}
                    </Typography>
                  ) : null}

                  {button ? (
                    <div data-motion-button>
                      <Button href={button?.url} link variant="contained">
                        {button?.title}
                      </Button>
                    </div>
                  ) : null}
                </div>
              </div>
            </Grid>
          </Container>
        </section>
      )}
    </Motion>
  )
}

export default PushParagraph
